import React, { forwardRef, useState, useImperativeHandle, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Button, useMediaQuery, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Componente mejorado para firma digital que funciona bien en móviles
 * Versión optimizada para rendimiento máximo
 */
const ResponsiveSignaturePad = forwardRef(({
  label = 'Firma',
  onSignatureChange = null,
  initialSignature = null,
  height = 150,
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasStrokesRef = useRef(!!initialSignature);
  const lastPointRef = useRef(null);
  const [signatureExists, setSignatureExists] = useState(!!initialSignature);

  const canvasHeight = isMobile ? Math.round(height * 0.8) : height;

  const getCanvasContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  }, []);

  const paintBackground = useCallback((ctx, width, drawHeight) => {
    ctx.clearRect(0, 0, width, drawHeight);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, drawHeight);
  }, []);

  const configureContext = useCallback((ctx) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = isMobile ? 1.8 : 2.2;
    ctx.strokeStyle = '#000000';
  }, [isMobile]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const previousData = hasStrokesRef.current ? canvas.toDataURL('image/png') : null;
    const cssWidth = Math.max(280, Math.floor(container.clientWidth));
    const cssHeight = canvasHeight;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.floor(cssWidth * dpr);
    canvas.height = Math.floor(cssHeight * dpr);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const ctx = getCanvasContext();
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paintBackground(ctx, cssWidth, cssHeight);
    configureContext(ctx);

    if (previousData) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, cssWidth, cssHeight);
      };
      img.src = previousData;
    }
  }, [canvasHeight, getCanvasContext, paintBackground, configureContext]);

  useEffect(() => {
    resizeCanvas();
    if (!containerRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      resizeCanvas();
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [resizeCanvas]);

  // Limpiar firma - memoizado
  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = getCanvasContext();
    if (!canvas || !ctx) return;

    paintBackground(ctx, canvas.clientWidth, canvas.clientHeight);
    configureContext(ctx);
    isDrawingRef.current = false;
    lastPointRef.current = null;
    hasStrokesRef.current = false;
    setSignatureExists(false);
    if (onSignatureChange) onSignatureChange(null);
  }, [getCanvasContext, paintBackground, configureContext, onSignatureChange]);

  // Verificar si está vacío
  const isEmpty = useCallback(() => {
    return !hasStrokesRef.current;
  }, []);

  // Obtener firma como data URL
  const getSignatureDataUrl = useCallback(() => {
    if (!canvasRef.current || !hasStrokesRef.current) {
      return null;
    }
    return canvasRef.current.toDataURL('image/png');
  }, []);

  // Establecer firma desde una URL
  const setSignature = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    const ctx = getCanvasContext();
    if (!canvas || !ctx || !dataUrl) return;

    const img = new Image();
    img.onload = () => {
      paintBackground(ctx, canvas.clientWidth, canvas.clientHeight);
      configureContext(ctx);
      ctx.drawImage(img, 0, 0, canvas.clientWidth, canvas.clientHeight);
      hasStrokesRef.current = true;
      setSignatureExists(true);
    };
    img.src = dataUrl;
  }, [getCanvasContext, paintBackground, configureContext]);

  const getRelativePoint = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }, []);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const ctx = getCanvasContext();
    const point = getRelativePoint(event);
    if (!ctx || !point) return;

    event.preventDefault();
    canvasRef.current?.setPointerCapture?.(event.pointerId);
    isDrawingRef.current = true;
    lastPointRef.current = point;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  }, [getCanvasContext, getRelativePoint]);

  const handlePointerMove = useCallback((event) => {
    if (!isDrawingRef.current) return;

    const ctx = getCanvasContext();
    const point = getRelativePoint(event);
    const last = lastPointRef.current;
    if (!ctx || !point || !last) return;

    event.preventDefault();
    const midX = (last.x + point.x) / 2;
    const midY = (last.y + point.y) / 2;
    ctx.quadraticCurveTo(last.x, last.y, midX, midY);
    ctx.stroke();

    hasStrokesRef.current = true;
    lastPointRef.current = point;
  }, [getCanvasContext, getRelativePoint]);

  const handlePointerUp = useCallback(() => {
    if (!isDrawingRef.current) return;

    const ctx = getCanvasContext();
    if (ctx) {
      ctx.closePath();
    }

    isDrawingRef.current = false;
    lastPointRef.current = null;

    if (hasStrokesRef.current) {
      setSignatureExists(true);
      if (onSignatureChange) {
        setTimeout(() => {
          onSignatureChange(getSignatureDataUrl());
        }, 0);
      }
    }
  }, [getCanvasContext, getSignatureDataUrl, onSignatureChange]);

  // Exponer métodos a través del ref
  useImperativeHandle(ref, () => ({
    clear,
    isEmpty,
    getSignatureDataUrl,
    setSignature,
    getCanvas: () => canvasRef.current || null
  }), [clear, isEmpty, getSignatureDataUrl, setSignature]);

  // Cargar firma inicial si existe
  useEffect(() => {
    if (initialSignature) {
      setSignature(initialSignature);
    }
  }, [initialSignature, setSignature]);

  // Manejar limpieza de firma
  const handleClearSignature = useCallback(() => {
    clear();
  }, [clear]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography 
        variant={isMobile ? "subtitle1" : "h6"} 
        gutterBottom
        sx={{ fontWeight: 'medium', mb: 1 }}
      >
        {label}
      </Typography>
      
      <Box sx={{ 
        position: 'relative',
        bgcolor: '#ffffff', 
        border: '2px solid #c46494', 
        borderRadius: 1,
        height: canvasHeight,
        mb: 2,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }} ref={containerRef}>
        {!signatureExists && (
          <Typography 
            variant={isMobile ? "body2" : "body1"}
            sx={{
              position: 'absolute',
              color: '#aaa',
              pointerEvents: 'none',
              textAlign: 'center',
              zIndex: 0
            }}
          >
            {isMobile ? 'Toca para firmar' : 'Haz clic y arrastra para firmar'}
          </Typography>
        )}
        
        <Box
          component="canvas"
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
            cursor: 'crosshair',
            touchAction: 'none',
            msTouchAction: 'none',
            backgroundColor: '#fff'
          }}
        />
      </Box>
      
      <Button 
        variant="outlined"
        size={isMobile ? "small" : "medium"}
        startIcon={<ClearIcon />}
        onClick={handleClearSignature}
        disabled={isEmpty()}
        sx={{ 
          mb: 1,
          minWidth: isMobile ? '80px' : '120px',
        }}
      >
        {isMobile ? 'Limpiar' : 'Limpiar Firma'}
      </Button>
    </Box>
  );
});

ResponsiveSignaturePad.displayName = 'ResponsiveSignaturePad';

export default ResponsiveSignaturePad;
